import { Workflow, Grid3X3, Users, ListOrdered, BarChart2, Tag } from 'lucide-react';
import { AreaDocLayout, DocDivider, ReferenceTable } from '../components/AreaDocLayout';
import { DocSection } from '../components/DocSection';
import { ConceptCard } from '../components/ConceptCard';
import { StepGuide } from '../components/StepGuide';
import { TrackGeometryDiagram } from '../components/diagrams/TrackGeometryDiagram';
import { useDiagramTheme } from '../components/diagrams/useDiagramTheme';

function TrackDiagram() {
  const t = useDiagramTheme();
  return (
    <div className="w-full border p-5 overflow-x-auto transition-colors" style={{ background: t.canvasBg, borderColor: t.canvasBorder }}>
      <svg viewBox="0 0 680 140" className="w-full" style={{ minWidth: 500 }}>
        <defs>
          <marker id="ti-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L7,3 z" fill={t.arrowColor} />
          </marker>
        </defs>

        {/* Comparison Cell */}
        <rect x="10" y="30" width="150" height="80" rx="8" fill={t.violetBg} stroke={t.violetBorder} strokeWidth="1.5" />
        <text x="85" y="56" textAnchor="middle" fontSize="11" fontWeight="700" fill={t.violetText} fontFamily="sans-serif">Comparison Cell</text>
        <text x="85" y="72" textAnchor="middle" fontSize="9" fill={t.violetText} fontFamily="sans-serif">Category × Altitude</text>
        <text x="85" y="88" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">Pure geometry</text>
        <text x="85" y="100" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">No time or scope</text>

        {/* Plus Cycle */}
        <text x="172" y="74" textAnchor="middle" fontSize="16" fill={t.arrowColor} fontFamily="sans-serif">+</text>

        <rect x="185" y="40" width="120" height="60" rx="8" fill={t.indigoBg} stroke={t.indigoBorder} strokeWidth="1.5" />
        <text x="245" y="64" textAnchor="middle" fontSize="11" fontWeight="700" fill={t.indigoText} fontFamily="sans-serif">Cycle + Scope</text>
        <text x="245" y="80" textAnchor="middle" fontSize="9" fill={t.nodeSubText} fontFamily="sans-serif">Specific time window</text>
        <text x="245" y="93" textAnchor="middle" fontSize="9" fill={t.nodeSubText} fontFamily="sans-serif">Organizational scope</text>

        {/* Equals */}
        <text x="317" y="74" textAnchor="middle" fontSize="16" fill={t.arrowColor} fontFamily="sans-serif">=</text>

        {/* Track */}
        <rect x="330" y="15" width="330" height="110" rx="8" fill={t.skyBg} stroke={t.skyBorder} strokeWidth="2" />
        <text x="495" y="38" textAnchor="middle" fontSize="11" fontWeight="700" fill={t.skyText} fontFamily="sans-serif">Track</text>
        <text x="495" y="53" textAnchor="middle" fontSize="9" fill={t.skyText} fontFamily="sans-serif">Comparison Cell in a specific Cycle &amp; Scope</text>

        {/* Things owned by track */}
        <rect x="342" y="60" width="72" height="52" rx="5" fill={t.nodeBg} stroke={t.skyInnerBorder} strokeWidth="1" />
        <text x="378" y="80" textAnchor="middle" fontSize="9" fontWeight="600" fill={t.skyText} fontFamily="sans-serif">Sessions</text>
        <text x="378" y="94" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">Config +</text>
        <text x="378" y="104" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">ranking</text>

        <rect x="422" y="60" width="72" height="52" rx="5" fill={t.nodeBg} stroke={t.skyInnerBorder} strokeWidth="1" />
        <text x="458" y="80" textAnchor="middle" fontSize="9" fontWeight="600" fill={t.skyText} fontFamily="sans-serif">Results</text>
        <text x="458" y="94" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">Immutable</text>
        <text x="458" y="104" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">ranked output</text>

        <rect x="502" y="60" width="72" height="52" rx="5" fill={t.nodeBg} stroke={t.skyInnerBorder} strokeWidth="1" />
        <text x="538" y="80" textAnchor="middle" fontSize="9" fontWeight="600" fill={t.skyText} fontFamily="sans-serif">Proposals</text>
        <text x="538" y="94" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">Per loop,</text>
        <text x="538" y="104" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">governance</text>

        <rect x="582" y="60" width="66" height="52" rx="5" fill={t.nodeBg} stroke={t.skyInnerBorder} strokeWidth="1" />
        <text x="615" y="80" textAnchor="middle" fontSize="9" fontWeight="600" fill={t.skyText} fontFamily="sans-serif">Posture</text>
        <text x="615" y="94" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">Governance</text>
        <text x="615" y="104" textAnchor="middle" fontSize="8" fill={t.nodeSubText} fontFamily="sans-serif">state</text>
      </svg>
      <p className="text-center text-xs mt-1" style={{ color: t.canvasText }}>A Track = Comparison Cell geometry placed in the context of a specific cycle and scope</p>
    </div>
  );
}

export default function TracksDocs() {
  return (
    <AreaDocLayout
      icon={Workflow}
      iconBg="bg-[var(--slate-dim)] border-[var(--slate)]"
      iconColor="text-[var(--text-ter)]"
      title="Tracks"
      subtitle="Comparability geometry — defining which items can be meaningfully ranked against each other."
      tag="Comparability"
      tagColor="bg-[var(--slate-dim)] text-[var(--text-sec)]"
    >
      {/* Overview */}
      <DocSection
        title="Overview"
        description="Tracks are the unit of comparability — they define the boundary inside which ranking is meaningful."
        accent="none"
      >
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 text-sm text-[var(--text-sec)] leading-relaxed">
          A <strong>Comparison Cell</strong> is pure geometry: <em>Item Category × Altitude</em>. It defines which items are meaningfully comparable to each other. There is no time, scope, or execution on a Comparison Cell — it is simply a boundary definition.
          A <strong>Track</strong> brings that geometry to life inside a specific cycle and scope. Tracks own sessions, results, and proposals; within a Track, uniformity is enforced by its Session Baseline (the locked items, criteria, and value ranges).
          Items in different Comparison Cells are never compared to each other — this is a structural constraint, not a policy.
        </div>
      </DocSection>

      {/* Geometry diagram */}
      <DocSection
        title="Comparison Cell Geometry"
        description="The Category × Altitude matrix that defines which items belong in the same comparison space."
        accent="none"
      >
        <TrackGeometryDiagram />
      </DocSection>

      {/* Track diagram */}
      <DocSection
        title="From Comparison Cell to Track"
        description="When a Comparison Cell is activated inside a cycle and scope, it becomes a Track with full execution capability."
        accent="none"
      >
        <TrackDiagram />
      </DocSection>

      <DocDivider />

      {/* Key concepts */}
      <DocSection title="Key Concepts" accent="none">
        <div className="grid gap-3 md:grid-cols-2">
          <ConceptCard
            term="Comparison Cell"
            definition="Pure comparability geometry: Item Category × Altitude. Defines which items can be meaningfully compared. Has no cycle, scope, time, or execution state. Reusable across cycles."
            icon={Grid3X3}
            color="gray"
            tag="Geometry"
          />
          <ConceptCard
            term="Track"
            definition="A Comparison Cell placed in the context of a specific cycle and scope. Owns sessions, results, proposals per loop, and a governance posture."
            icon={Workflow}
            color="gray"
            tag="Execution"
          />
          <ConceptCard
            term="Item Pool"
            definition="The set of items assigned to a Track for a given cycle. Items in the pool participate in the sessions and are ranked against each other."
            icon={ListOrdered}
            color="gray"
          />
          <ConceptCard
            term="Governance Posture"
            definition="The current governance state of a Track, derived from the latest proposal state within the active catchball loop."
            icon={BarChart2}
            color="gray"
            tag="Derived"
          />
          <ConceptCard
            term="Comparison Cohort"
            definition="A grouping of Tracks sharing the same Comparison Cell across cycles or scopes. Used for reporting and for the cycle's cell allocation read model, never for governance."
            icon={Tag}
            color="gray"
            tag="Grouping"
          />
          <ConceptCard
            term="Parallel Tracks"
            definition="A cycle can contain multiple Tracks in parallel. Each has independent sessions, results, and governance outcomes — acceptance in one does not affect another."
            icon={Users}
            color="gray"
          />
        </div>
      </DocSection>

      <DocDivider />

      {/* The three constructs clarified */}
      <DocSection
        title="Comparison Cell vs. Track vs. Comparison Cohort"
        description="These three constructs are often confused. Here is the precise distinction."
        accent="none"
      >
        <div className="border border-[var(--rule)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--surface)] border-b border-[var(--rule)]">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-36">Construct</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-28">Layer</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading">What it is</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-[var(--text-sec)] font-heading w-32">Has governance?</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Comparison Cell', layer: 'Geometry', desc: 'Category × Altitude boundary. Defines comparability. Timeless, scopeless.', gov: 'No' },
                { name: 'Track', layer: 'Execution', desc: 'Comparison Cell + cycle + scope. The unit that owns sessions, results, proposals, and governance posture.', gov: 'Yes' },
                { name: 'Comparison Cohort', layer: 'Grouping', desc: 'Tracks sharing the same Comparison Cell across cycles or scopes. Reporting and the cell allocation read model; never ranks across Tracks.', gov: 'No' },
              ].map((r, i) => (
                <tr key={r.name} className={i % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/50'}>
                  <td className="px-4 py-2.5 text-xs font-semibold text-[var(--text-ter)] font-heading">{r.name}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--text-ter)]">{r.layer}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--text-sec)]">{r.desc}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--text-ter)]">{r.gov}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DocSection>

      <DocDivider />

      {/* Common tasks */}
      <DocSection title="Common Tasks" accent="none">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 max-w-lg">
          <p className="text-sm font-semibold text-[var(--text-pri)] font-heading mb-3">Add a Track to a Cycle</p>
          <StepGuide color="gray" steps={[
            { title: 'Open the cycle', description: 'Navigate to Cycles and open the cycle you are configuring.' },
            { title: 'Go to the Tracks tab', description: 'The Tracks panel shows current tracks and allows adding new ones.' },
            { title: 'Select Category and Altitude', description: 'Choose the item category and organizational altitude that defines this track\'s comparability boundary.' },
            { title: 'Set scope', description: 'Define which organizational units or areas are in scope for this Track.' },
            { title: 'Confirm', description: 'The Track is created. You can now add sessions, configure the pool, and begin work.', note: 'Tracks can be added throughout the design phase.' },
          ]} />
        </div>
      </DocSection>

      <DocDivider />

      {/* Reference */}
      <DocSection title="Track Field Reference" accent="none">
        <ReferenceTable rows={[
          { field: 'id', type: 'uuid', description: 'Unique Track identifier.' },
          { field: 'cycle_id', type: 'uuid', description: 'The cycle this Track belongs to.' },
          { field: 'track_cell_id', type: 'uuid', description: 'The Comparison Cell (Category × Altitude) this Track activates.' },
          { field: 'scope', type: 'object', description: 'Scope definition for this Track — which teams/areas are included.' },
          { field: 'governance_posture', type: 'enum', description: 'Current governance state derived from the latest proposal in the active loop.' },
          { field: 'workspace_id', type: 'uuid', description: 'Workspace this Track belongs to.' },
          { field: 'created_at', type: 'timestamp', description: 'When the Track was created.' },
        ]} />
      </DocSection>
    </AreaDocLayout>
  );
}
